import { useEffect, useState } from "react";
import { Layer, Source } from "react-map-gl";
import { FeatureCollection } from "geojson";
import { getImpactLayerConfig } from "./geoUtils";
import {
    decrementOngoingFetches,
    incrementOngoingFetches,
    setHasSlowConnection,
    useHasSlowConnection,
} from "../../features/time/playbackSlice";
import { useDispatch } from "react-redux";
import { useForecastTime, useSelectedChip, useSelectedImpactSurface } from "../../features/surface/surfaceSlice";
import { useLayerVisibilityState, useOpacity } from "../../features/mapDisplay/mapDisplaySlice";
import { RouteViewState, useRouteViewState } from "../../features/riskViewer/riskViewerSlice";

type Props = {
    refetchRiskData: ( tauTimeout: boolean ) => void,
};

export default function ImpactDataLayer( { refetchRiskData }: Props )
{
    const dispatch = useDispatch();
    const [ impactData, setImpactData ] = useState<FeatureCollection>( { type: "FeatureCollection", features: [] } );

    const currentSurface = useSelectedImpactSurface();
    const hasSlowConnection = useHasSlowConnection();
    const selectedChip = useSelectedChip();
    const opacity = useOpacity();
    const isLayerEnabledTracker = useLayerVisibilityState();
    const forecastTime = useForecastTime();
    const routeViewState = useRouteViewState();

    const impactLayer = getImpactLayerConfig( selectedChip, opacity, isLayerEnabledTracker, "impact" );

    useEffect( () =>
    {
        setImpactData( { type: "FeatureCollection", features: [] } );
    }, [ forecastTime ] );

    useEffect( () =>
    {
        const controller = new AbortController();
        const signal = controller.signal;
        let isCancelled = false;

        const urls = currentSurface?.impactUrls?.urls;
        if ( urls === undefined || routeViewState === RouteViewState.COLORED_ROUTE )
        {
            {
                setImpactData( {
                    type: "FeatureCollection",
                    features: []
                } );
            }
            return;
        }

        const fetchWith403Handling = async ( url: string ) =>
        {
            const res = await fetch( url, { signal } );
            if ( res.status === 403 )
            {
                refetchRiskData( true );
                throw new Error( "403 Forbidden" );
            }
            return res.json();
        };

        const fetchPromises = [ fetchWith403Handling( urls[0] ) ];
        if ( urls[1] !== undefined ) fetchPromises.push( fetchWith403Handling( urls[1] ) );

        dispatch( incrementOngoingFetches() );

        const slowTimeout = setTimeout( () =>
        {
            if ( !isCancelled ) dispatch( setHasSlowConnection() );
        }, 1500 );

        Promise.all( fetchPromises )
            .then( ( [ data1, data2 ] ) =>
            {
                if ( isCancelled ) return;

                const mergedFeatures = Array.isArray( data2 )
                    ? [ ...data1, ...data2 ]
                    : data1;

                setImpactData( {
                    type: "FeatureCollection",
                    features: mergedFeatures,
                } );

                clearTimeout( slowTimeout );
            } )
            .catch( ( err ) =>
            {
                if ( err.name !== "AbortError" )
                {
                    console.error( "Impact data fetch error:", err );
                }
            } )
            .finally( () =>
            {
                dispatch( decrementOngoingFetches() );
            } );

        return () =>
        {
            isCancelled = true;
            if ( hasSlowConnection ) controller.abort();
        };
    }, [ currentSurface ] );

    return (
        <Source id = "impact-data" type = "geojson" data = {impactData}>
            <Layer {...impactLayer}/>
        </Source>
    );
}
